import { useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from 'designSystem/components/DropdownMenu'
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from 'designSystem/components/Sidebar'
import { useOpenEvaluationsTab } from 'features/evaluations/hooks/useOpenEvaluationsTab'
import { EVALUATIONS_PERMISSIONS } from 'features/evaluations/perms/evaluationsPermissions'
import { WORKSPACE_SETTINGS_DESTINATION } from 'features/navbar/configSettings/workspaceSettingsLink'
import { LanguageToggle } from 'features/navbar/LanguageToggle'
import { StyledDropdownMenuTrigger } from 'features/navbar/NavbarDropdown'
import { PermissionGateToggle } from 'features/navbar/PermissionGateToggle'
import { useSiteSelectorCapabilities } from 'features/navbar/siteSelectors/siteSelectorCapabilities'
import { UnitsToggle } from 'features/navbar/UnitsToggle'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import { createAccessSearch } from 'routes/access/-searchParams'
import styled from 'styled-components'
import {
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_UPLOADER,
} from 'types'

import { ProfileAndSettingsBase } from './ProfileAndSettingsBase'
import { type ProfileAndSettingsProps } from './useProfileAndSettingsState'

const SUPPORT_EMAIL_HREF = 'mailto:support@geckorobotics.com'

const SidebarProfileAvatar = styled.span`
  position: relative;
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.activeText};

  &::before {
    content: '';
    position: absolute;
    width: 1.5rem;
    height: 1.5rem;
    border: 1px solid ${({ theme }) => theme.forecast.border};
    border-radius: var(--radius);
  }

  svg {
    position: relative;
    width: 1.125rem;
    height: 1.125rem;
  }
`

const SidebarProfileName = styled.span`
  transition: opacity 0.15s ease;

  [data-collapsible='icon'] & {
    opacity: 0;
  }
`

const SidebarProfileMenuContent = styled(DropdownMenuContent)`
  width: 15rem;
  border-radius: 0.75rem;
  padding: 0.5rem;
`

const SidebarProfileMenuItem = styled(DropdownMenuItem)`
  padding: 0.5rem;
  font-size: 0.75rem;
  text-decoration: none;
`

const EvaluationsSidebarItem: React.FC = () => {
  const { t } = useLingui()
  const handleSelect = useOpenEvaluationsTab()

  return (
    <SidebarProfileMenuItem onSelect={handleSelect}>
      {t`Evaluations`}
    </SidebarProfileMenuItem>
  )
}

export const ProfileAndSettingsSidebar: React.FC<ProfileAndSettingsProps> = ({
  onOpenUploadDocuments,
  orgID,
  siteID,
  onWorkspaceNavigate,
}) => {
  const { t } = useLingui()
  const { canManageAccess } = useSiteSelectorCapabilities(orgID, siteID)
  const handleManageAccess = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onWorkspaceNavigate?.(event, {
      label: t`Manage Site Access`,
      destinationKey: WORKSPACE_SETTINGS_DESTINATION.manageAccess,
    })
  }
  return (
    <ProfileAndSettingsBase
      renderTrigger={(state) => {
        const userDisplayName = state.userDisplayName
        return (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={state.isModalOpen}
              tooltip={userDisplayName}
              variant='embedded'>
              <StyledDropdownMenuTrigger
                type='button'
                aria-label={t`Open profile settings menu for ${userDisplayName}`}>
                <SidebarProfileAvatar>
                  <LucideIcon name='User' />
                </SidebarProfileAvatar>
                <SidebarProfileName>{userDisplayName}</SidebarProfileName>
              </StyledDropdownMenuTrigger>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )
      }}
      renderContent={(state) => (
        <SidebarProfileMenuContent
          side='top'
          sideOffset={4}
          align='start'
          width='auto'>
          {state.shouldShowUnits && <UnitsToggle />}
          <LanguageToggle />
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <PermissionGateToggle />
          </PermissionsGate>
          <DropdownMenuSeparator />
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <SidebarProfileMenuItem asChild>
              <Link to='/design-system-library'>{t`Design System Library`}</Link>
            </SidebarProfileMenuItem>
          </PermissionsGate>
          {canManageAccess ? (
            <SidebarProfileMenuItem asChild>
              <Link
                to='/access'
                search={createAccessSearch({ orgId: orgID, siteId: siteID })}
                onClick={handleManageAccess}>
                {t`Manage Site Access`}
              </Link>
            </SidebarProfileMenuItem>
          ) : null}
          {onOpenUploadDocuments ? (
            <PermissionsGate
              requires={[
                PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
                PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
                ROLE_CANTILEVER_DOCUMENT_NAVIGATOR_UPLOADER,
              ]}>
              <SidebarProfileMenuItem onSelect={onOpenUploadDocuments}>
                {t`Upload Documents`}
              </SidebarProfileMenuItem>
            </PermissionsGate>
          ) : null}
          <PermissionsGate requires={EVALUATIONS_PERMISSIONS}>
            <EvaluationsSidebarItem />
          </PermissionsGate>
          <SidebarProfileMenuItem onSelect={() => state.setShowToSModal(true)}>
            {t`Terms of Service`}
          </SidebarProfileMenuItem>
          <SidebarProfileMenuItem asChild>
            <a href={SUPPORT_EMAIL_HREF}>{t`Email Support`}</a>
          </SidebarProfileMenuItem>
          <SidebarProfileMenuItem
            variant='destructive'
            onSelect={state.handleLogOut}>
            {t`Log out`}
          </SidebarProfileMenuItem>
        </SidebarProfileMenuContent>
      )}
    />
  )
}
